// src/pages/artisan/MyProjects.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import withAuth from '../../auth/WithAuth';
import './artisanupdates.scss';
import Sidebar from '../../../components/layout/Sidebar';
import { Badge, Modal, Button, Spinner } from 'react-bootstrap';

import {
  fetchArtisanAcceptedProjects,
  setSelectedProjectForDetails,
} from '../../../redux/slices/projectsSlice';
import { motion, AnimatePresence } from 'framer-motion';
import { FaComments, FaEnvelope } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

const MyProjects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeImage, setActiveImageState] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const { projects, selectedProjectForDetails, loading } = useSelector(
    (state) => state.projects,
  );

  const [filter, setFilter] = useState('All');
  const [isMarkingRead, setIsMarkingRead] = useState(false);

  // Fetch projects on mount
  useEffect(() => {
    dispatch(fetchArtisanAcceptedProjects());
  }, [dispatch]);

  // Reset active image when selected project changes
  useEffect(() => {
    setActiveImageState(0);
  }, [selectedProjectForDetails]);

  // Handle opening messages page
  const handleOpenMessages = async (project) => {
    navigate('/messaging');
  };

  // Helper function to get display status
  const getDisplayStatus = (status) => {
    if (!status) return 'Unknown';

    const statusMap = {
      'in-progress': 'In Progress',
      'in progress': 'In Progress',
      completed: 'Completed',
      'awaiting-deposit-payment': 'Awaiting Deposit Payment',
      'awaiting deposit payment': 'Awaiting Deposit Payment',
      'awaiting-final-payment': 'Awaiting Final Payment',
      'awaiting final payment': 'Awaiting Final Payment',
      'payment pending': 'Payment Pending',
      'pending payment': 'Payment Pending',
      open: 'Open',
      pending: 'Pending',
      'awaiting artisan': 'Awaiting Artisan',
      'awaiting-artisan': 'Awaiting Artisan',
      active: 'Active',
    };

    const lowerStatus = status.toLowerCase();
    return statusMap[lowerStatus] || status;
  };

  // Helper function to get status badge variant
  const getStatusBadgeVariant = (status) => {
    if (!status) return 'secondary';

    const statusMap = {
      'in-progress': 'warning',
      'in progress': 'warning',
      completed: 'success',
      'awaiting-deposit-payment': 'info',
      'awaiting deposit payment': 'info',
      'awaiting-final-payment': 'primary',
      'awaiting final payment': 'primary',
      'payment pending': 'warning',
      'pending payment': 'warning',
      open: 'secondary',
      pending: 'secondary',
      'awaiting artisan': 'info',
      'awaiting-artisan': 'info',
      active: 'success',
    };

    const lowerStatus = status.toLowerCase();
    return statusMap[lowerStatus] || 'secondary';
  };

  // Helper function to get status dot class
  const getStatusDotClass = (status) => {
    if (!status) return 'unknown';

    const statusMap = {
      'in-progress': 'in-progress',
      'in progress': 'in-progress',
      completed: 'completed',
      'awaiting-deposit-payment': 'awaiting-deposit',
      'awaiting deposit payment': 'awaiting-deposit',
      'awaiting-final-payment': 'awaiting-final',
      'awaiting final payment': 'awaiting-final',
      'payment pending': 'pending',
      'pending payment': 'pending',
      open: 'open',
      pending: 'pending',
      'awaiting artisan': 'awaiting-artisan',
      'awaiting-artisan': 'awaiting-artisan',
      active: 'active',
    };

    const lowerStatus = status.toLowerCase();
    return statusMap[lowerStatus] || 'unknown';
  };

  // Get filter options based on available statuses
  const getFilterOptions = () => {
    const options = ['All'];
    const uniqueStatuses = new Set();

    projects?.forEach((project) => {
      if (project.status) {
        const displayStatus = getDisplayStatus(project.status);
        uniqueStatuses.add(displayStatus);
      }
    });

    return [...options, ...Array.from(uniqueStatuses)];
  };

  const filteredProjects =
    projects?.filter((p) => {
      if (filter === 'All') return true;
      return getDisplayStatus(p.status) === filter;
    }) || [];

  // Check if project is in a payment waiting state
  const isAwaitingDeposit = (status) => {
    return (
      status?.toLowerCase() === 'awaiting-deposit-payment' ||
      status?.toLowerCase() === 'awaiting deposit payment'
    );
  };

  const isAwaitingFinalPayment = (status) => {
    return (
      status?.toLowerCase() === 'awaiting-final-payment' ||
      status?.toLowerCase() === 'awaiting final payment'
    );
  };

  const isCompleted = (status) => {
    return status?.toLowerCase() === 'completed';
  };

  const isInProgress = (status) => {
    return (
      status?.toLowerCase() === 'in-progress' ||
      status?.toLowerCase() === 'in progress' ||
      status?.toLowerCase() === 'active'
    );
  };

  return (
    <>
      <Sidebar />
      <div className="modern-dashboard my-projects-page">
        {/* Hero Section */}
        <section className="hero-banner">
          <div className="hero-pattern"></div>
          <div className="hero-overlay">
            <div className="hero-content">
              <h1>
                <span className="gradient-text">🛠️ Ongoing</span> Projects
              </h1>
              <p>
                View Your Ongoing Projects and Communicate With Clients On
                Project Progress
              </p>
            </div>
          </div>
        </section>

        {/* Filter Tabs */}
        <div className="nav-pills">
          {getFilterOptions().map((tab) => (
            <button
              key={tab}
              className={`nav-link ${filter === tab ? 'active' : ''}`}
              onClick={() => setFilter(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="projects-grid container">
          {loading ? (
            <div className="loading-state-modern">
              <div className="spinner-modern"></div>
              <p>Loading projects...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No projects in this category</h3>
              <p>Switch tabs to see other projects.</p>
            </div>
          ) : (
            <AnimatePresence>
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className="project-card"
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.1,
                    type: 'spring',
                    stiffness: 100,
                  }}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                >
                  {/* Notification badge */}
                  {project.unread_updates > 0 && (
                    <div className="notification-badge-left">
                      <Badge bg="danger" pill className="unread-badge-pulse">
                        <FaComments className="me-1" />
                        {project.unread_updates > 99
                          ? '99+'
                          : project.unread_updates}
                      </Badge>
                    </div>
                  )}

                  <div className="card-image-container">
                    {project.images?.length > 0 ? (
                      <img
                        src={project.images[0]}
                        alt={project.title}
                        className="card-image"
                      />
                    ) : (
                      <div className="no-image-placeholder">
                        <div className="placeholder-icon">🛠️</div>
                        <span>No Image</span>
                      </div>
                    )}
                    <div className="card-overlay">
                      <div className="status-badge">
                        <span
                          className={`status-dot ${getStatusDotClass(
                            project.status,
                          )}`}
                        ></span>
                        {getDisplayStatus(project.status)}
                      </div>
                    </div>
                  </div>

                  <div className="card-content">
                    <h3 className="card-title">{project.title}</h3>

                    <div className="card-meta">
                      <div className="meta-item">
                        <span className="meta-label">Client</span>
                        <span className="meta-value">{project.client}</span>
                      </div>

                      <div className="meta-item">
                        <span className="meta-label">Deadline</span>
                        <span className="meta-value">
                          {project.deadline
                            ? new Date(project.deadline).toLocaleDateString()
                            : 'Not set'}
                        </span>
                      </div>

                      <div className="meta-item">
                        <span className="meta-label">Budget</span>
                        <span className="meta-value">
                          {project.agreed_cost ? (
                            <>
                              KSh{' '}
                              {parseFloat(
                                project.agreed_cost || 0,
                              ).toLocaleString()}
                            </>
                          ) : (
                            <>
                              KSh{' '}
                              {parseFloat(project.budget || 0).toLocaleString()}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Show progress only for in-progress projects */}
                  {isInProgress(project.status) && (
                    <>
                      <div className="progress-section">
                        <div className="progress-header">
                          <span>Progress</span>
                          <span className="progress-percent">
                            {project.progress || 0}%
                          </span>
                        </div>
                        <div className="custom-progress">
                          <div
                            className="progress-bar"
                            style={{ width: `${project.progress || 0}%` }}
                          />
                        </div>
                      </div>

                      <div className="card-actions">
                        <button
                          className="btn view-btn"
                          onClick={() =>
                            dispatch(setSelectedProjectForDetails(project))
                          }
                        >
                          <i className="bi bi-eye me-1"></i> View Details
                        </button>
                        <button
                          className={`btn chat-btn ${
                            project.unread_updates > 0 ? 'has-unread' : ''
                          }`}
                          onClick={() => handleOpenMessages(project)}
                          disabled={isMarkingRead}
                        >
                          <FaEnvelope className="me-1" />
                          {project.unread_updates > 0
                            ? `Messages (${project.unread_updates})`
                            : 'Send Updates'}
                        </button>
                      </div>
                    </>
                  )}

                  {/* Awaiting Deposit Payment */}
                  {isAwaitingDeposit(project.status) && (
                    <div className="card-actions awaiting-deposit">
                      <div className="awaiting-deposit-wrapper">
                        <div className="awaiting-deposit-message">
                          <div className="message-icon-wrapper">
                            <span className="pulse-icon">💰</span>
                          </div>
                          <div className="message-text-wrapper">
                            <h5>Awaiting Deposit Payment</h5>
                            <p>
                              The client needs to complete the deposit payment
                              before work can begin.
                            </p>
                            <div className="status-tracker">
                              <span className="status-step active">
                                📋 Project Created
                              </span>
                              <span className="status-step active-pending">
                                💳 Payment Pending
                              </span>
                              <span className="status-step inactive">
                                🛠️ Work in Progress
                              </span>
                              <span className="status-step inactive">
                                ✅ Project Complete
                              </span>
                            </div>
                            <div className="payment-notice">
                              <span className="notice-icon">ℹ️</span>
                              <span>
                                You will be notified as soon as the client makes
                                the payment.
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          className="btn view-btn"
                          onClick={() =>
                            dispatch(setSelectedProjectForDetails(project))
                          }
                        >
                          <i className="bi bi-eye me-1"></i> View Details
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Awaiting Final Payment */}
                  {isAwaitingFinalPayment(project.status) && (
                    <div className="card-actions awaiting-final">
                      <div className="awaiting-final-wrapper">
                        <div className="awaiting-final-message">
                          <div className="message-icon-wrapper">
                            <span className="pulse-icon">🎉</span>
                          </div>
                          <div className="message-text-wrapper">
                            <h5>Project Complete - Awaiting Final Payment</h5>
                            <p>
                              Great work! The project is complete. Waiting for
                              the client to make the final payment.
                            </p>
                            <div className="status-tracker">
                              <span className="status-step active">
                                📋 Project Created
                              </span>
                              <span className="status-step active">
                                💰 Deposit Paid
                              </span>
                              <span className="status-step active">
                                🛠️ Work Complete
                              </span>
                              <span className="status-step active-pending">
                                💳 Final Payment Pending
                              </span>
                            </div>
                            <div className="payment-notice success">
                              <span className="notice-icon">✅</span>
                              <span>
                                Once the client pays, the project will be marked
                                as completed.
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          className="btn view-btn"
                          onClick={() =>
                            dispatch(setSelectedProjectForDetails(project))
                          }
                        >
                          <i className="bi bi-eye me-1"></i> View Details
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Completed Projects */}
                  {isCompleted(project.status) && (
                    <div className="card-actions completed">
                      <div className="completed-wrapper">
                        <div className="completed-message">
                          <div className="message-text-wrapper">
                            <div className="completion-badge">
                              <span className="badge-icon">⭐</span>
                              <span>Project Successfully Completed</span>
                              <span className="completion-date">
                                {project.date_of_completion
                                  ? new Date(
                                      project.date_of_completion,
                                    ).toLocaleDateString()
                                  : new Date().toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          className="btn view-btn"
                          onClick={() =>
                            dispatch(setSelectedProjectForDetails(project))
                          }
                        >
                          <i className="bi bi-eye me-1"></i> View Details
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* ============================================================
            PROJECT DETAILS MODAL
           ============================================================ */}
        <Modal
          show={!!selectedProjectForDetails}
          onHide={() => dispatch(setSelectedProjectForDetails(null))}
          size="lg"
          centered
          className="project-details-modal-modern"
        >
          <Modal.Header closeButton>
            <div>
              <Modal.Title className="mb-2">
                <span className="modal-title-icon">📋</span>{' '}
                {selectedProjectForDetails?.title}
              </Modal.Title>
              <div className="d-flex gap-2">
                <Badge
                  bg={getStatusBadgeVariant(selectedProjectForDetails?.status)}
                >
                  {getDisplayStatus(selectedProjectForDetails?.status)}
                </Badge>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body>
            {selectedProjectForDetails && (
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="mb-3">
                    <img
                      src={
                        (selectedProjectForDetails.images || [])[activeImage] ||
                        'https://via.placeholder.com/400x250?text=No+Image'
                      }
                      alt="Project"
                      className="img-fluid rounded"
                      style={{
                        height: '250px',
                        width: '100%',
                        objectFit: 'cover',
                        borderRadius: '12px',
                      }}
                    />
                  </div>
                  {(selectedProjectForDetails.images || []).length > 1 && (
                    <div className="thumbnails-active-projects d-flex gap-2 justify-content-center flex-wrap">
                      {(selectedProjectForDetails.images || []).map(
                        (img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Thumb ${idx + 1}`}
                            className={`rounded cursor-pointer ${
                              activeImage === idx ? 'border' : ''
                            }`}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                            }}
                            onClick={() => setActiveImageState(idx)}
                          />
                        ),
                      )}
                    </div>
                  )}
                </div>

                <div className="col-md-6 d-flex flex-column gap-3">
                  <div className="p-3 bg-light rounded shadow-sm">
                    <h6 className="text-muted mb-3">Project Information</h6>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Client</span>
                      <span className="fw-semibold">
                        {selectedProjectForDetails.client}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Budget</span>
                      <span className="fw-semibold">
                        {selectedProjectForDetails.agreed_cost ? (
                          <>
                            KSh{' '}
                            {parseFloat(
                              selectedProjectForDetails.agreed_cost || 0,
                            ).toLocaleString()}
                          </>
                        ) : (
                          <>
                            KSh{' '}
                            {parseFloat(
                              selectedProjectForDetails.budget || 0,
                            ).toLocaleString()}
                          </>
                        )}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Status</span>
                      <span className="fw-semibold">
                        {getDisplayStatus(selectedProjectForDetails.status)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Deadline</span>
                      <span className="fw-semibold text-danger">
                        {selectedProjectForDetails.deadline
                          ? new Date(
                              selectedProjectForDetails.deadline,
                            ).toLocaleDateString()
                          : 'Not set'}
                      </span>
                    </div>
                  </div>

                  <div className="p-3 bg-light rounded shadow-sm">
                    <h6 className="text-muted mb-2">Description</h6>
                    <p className="mb-0">
                      {selectedProjectForDetails.description ||
                        'No description available'}
                    </p>
                  </div>

                  {isInProgress(selectedProjectForDetails.status) && (
                    <div className="p-3 bg-light rounded shadow-sm">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="text-muted mb-0">Project Progress</h6>
                        <span className="fw-bold text-primary">
                          {selectedProjectForDetails.progress || 0}%
                        </span>
                      </div>
                      <div className="progress mb-3">
                        <div
                          className="progress-bar"
                          style={{
                            width: `${
                              selectedProjectForDetails.progress || 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Messages button in details modal */}
                  <div className="p-3 bg-light rounded shadow-sm">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="text-muted mb-0">Project Communication</h6>
                      <Button
                        className="project-communication"
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          dispatch(setSelectedProjectForDetails(null));
                          handleOpenMessages(selectedProjectForDetails);
                        }}
                        disabled={isMarkingRead}
                      >
                        <FaEnvelope className="me-1" />
                        {selectedProjectForDetails.unread_updates > 0
                          ? `Messages (${selectedProjectForDetails.unread_updates})`
                          : 'Messages'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => dispatch(setSelectedProjectForDetails(null))}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ============================================================
            IMAGE VIEWER MODAL
           ============================================================ */}
        <Modal
          show={!!selectedImage}
          onHide={() => setSelectedImage(null)}
          centered
          size="xl"
          className="image-viewer-modal"
          fullscreen="md-down"
        >
          <Modal.Body className="image-viewer-body p-0">
            {selectedImage && (
              <>
                <Button
                  variant="link"
                  className="viewer-close"
                  onClick={() => setSelectedImage(null)}
                >
                  <IoClose />
                </Button>
                <motion.img
                  key={selectedImage}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  src={selectedImage}
                  alt="Selected update"
                  className="viewer-image"
                />
                <div className="image-info">
                  <span className="image-index">Project Update Image</span>
                  <span className="image-size">Click outside to close</span>
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default withAuth(MyProjects, 'artisan');
